/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
/**
 * Tab Animation Hook
 *
 * Manages state and playback for tab animation on the fretboard.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  TabSequence,
  TabPosition,
  AnimationState,
  AnimationCommand
} from '../types/tabAnimation';

interface UseTabAnimationReturn {
  /** Current animation state */
  state: AnimationState;

  /** Currently highlighted positions on fretboard */
  currentPositions: TabPosition[];

  /** Dispatch a control command */
  dispatch: (command: AnimationCommand) => void;

  /** Load a new tab sequence */
  loadSequence: (sequence: TabSequence | null) => void;

  /** Current progress (0-1) */
  progress: number;

  /** Loaded sequence */
  sequence: TabSequence | null;
}

/**
 * Hook for managing tab animation playback
 */
export function useTabAnimation(): UseTabAnimationReturn {
  const [sequence, setSequence] = useState<TabSequence | null>(null);
  const [state, setState] = useState<AnimationState>({
    isPlaying: false,
    currentIndex: 0,
    currentBeat: 0,
    speedMultiplier: 0.5, // Default to half speed for easier learning
    loop: false
  });

  const animationFrameRef = useRef<number>();
  const lastUpdateTimeRef = useRef<number>(0);

  /**
   * Get positions that should be highlighted at current beat
   * Interpolates slides and bends to show in-between notes
   */
  const getCurrentPositions = useCallback((): TabPosition[] => {
    if (!sequence) return [];

    // Find positions that are currently sounding
    const activePositions: TabPosition[] = [];

    for (const pos of sequence.positions) {
      const posStart = pos.timing;
      const posEnd = pos.timing + pos.duration;

      // Check if this position is currently active
      if (state.currentBeat >= posStart && state.currentBeat < posEnd) {
        // Calculate progress through this note (0 to 1)
        const progress = (state.currentBeat - posStart) / pos.duration;

        // Handle slides and bends with targetFret
        if (pos.targetFret !== undefined && (pos.technique === 'slide' || pos.technique === 'bend' || pos.technique === 'hammer' || pos.technique === 'pull')) {
          // Interpolate between start fret and target fret
          const fretDiff = pos.targetFret - pos.fret;
          const interpolatedFret = Math.round(pos.fret + (fretDiff * progress));

          activePositions.push({
            ...pos,
            fret: interpolatedFret
          });
        } else {
          // Normal note - just show the fret
          activePositions.push(pos);
        }
      }
    }

    return activePositions;
  }, [sequence, state.currentBeat]);

  const currentPositions = getCurrentPositions();

  /**
   * Calculate progress through the sequence (0-1)
   */
  const progress = sequence
    ? sequence.totalBeats > 0
      ? Math.min(state.currentBeat / sequence.totalBeats, 1)
      : 0
    : 0;

  /**
   * Animation loop - updates current beat based on elapsed time
   */
  const animate = useCallback((timestamp: number) => {
    if (!sequence || !state.isPlaying) return;

    const deltaTime = lastUpdateTimeRef.current
      ? (timestamp - lastUpdateTimeRef.current) / 1000 // Convert to seconds
      : 0;

    lastUpdateTimeRef.current = timestamp;

    // Calculate how many beats have elapsed
    // BPM = beats per minute, so beats per second = BPM / 60
    const beatsPerSecond = (sequence.tempo * state.speedMultiplier) / 60;
    const deltaBeats = deltaTime * beatsPerSecond;

    setState(prev => {
      let newBeat = prev.currentBeat + deltaBeats;

      // Check if we've reached the end
      if (newBeat >= sequence.totalBeats) {
        if (prev.loop) {
          // Loop back to start
          newBeat = 0;
        } else {
          // Stop at end
          return {
            ...prev,
            isPlaying: false,
            currentBeat: sequence.totalBeats
          };
        }
      }

      // Find the current position index
      let newIndex = prev.currentIndex;
      while (
        newIndex < sequence.positions.length &&
        sequence.positions[newIndex].timing <= newBeat
      ) {
        newIndex++;
      }

      return {
        ...prev,
        currentBeat: newBeat,
        currentIndex: newIndex
      };
    });

    // Schedule next frame
    animationFrameRef.current = requestAnimationFrame(animate);
  }, [sequence, state.isPlaying, state.speedMultiplier, state.loop]);

  /**
   * Start/resume animation loop
   */
  useEffect(() => {
    if (state.isPlaying) {
      lastUpdateTimeRef.current = performance.now();
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [state.isPlaying, animate]);

  /**
   * Dispatch control commands
   */
  const dispatch = useCallback((command: AnimationCommand) => {
    switch (command.type) {
      case 'PLAY':
        setState(prev => ({
          ...prev,
          isPlaying: true
        }));
        break;

      case 'PAUSE':
        setState(prev => ({
          ...prev,
          isPlaying: false
        }));
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        break;

      case 'STOP':
        setState(prev => ({
          ...prev,
          isPlaying: false,
          currentBeat: 0,
          currentIndex: 0
        }));
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        break;

      case 'SEEK':
        setState(prev => {
          if (!sequence) return prev;

          const targetBeat = Math.max(
            0,
            Math.min(command.position * sequence.totalBeats, sequence.totalBeats)
          );

          // Find the index at this beat
          let newIndex = 0;
          while (
            newIndex < sequence.positions.length &&
            sequence.positions[newIndex].timing <= targetBeat
          ) {
            newIndex++;
          }

          return {
            ...prev,
            currentBeat: targetBeat,
            currentIndex: newIndex
          };
        });
        break;

      case 'SET_SPEED':
        setState(prev => ({
          ...prev,
          speedMultiplier: Math.max(0.25, Math.min(command.speed, 4.0))
        }));
        break;

      case 'TOGGLE_LOOP':
        setState(prev => ({
          ...prev,
          loop: !prev.loop
        }));
        break;
    }
  }, [sequence]);

  /**
   * Load a new sequence
   */
  const loadSequence = useCallback((newSequence: TabSequence | null) => {
    // Stop current playback
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    setSequence(newSequence);
    setState({
      isPlaying: false,
      currentIndex: 0,
      currentBeat: 0,
      speedMultiplier: 0.5, // Default to half speed for easier learning
      loop: false
    });
  }, []);

  return {
    state,
    currentPositions,
    dispatch,
    loadSequence,
    progress,
    sequence
  };
}
